import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import Masonry from "react-masonry-css";
import { Pencil, Save, X, Trash, Wand, RotateCcw, Files } from "lucide-react";
import { BaseAssignmentResponseDtoQuestionsItem } from "@packages/api/madlenAPI.schemas";
import { QuestionViewer } from "../../../SingleAssignment/componnets/questions/QuestionViewer";
import { QuestionEditor } from "../../../SingleAssignment/componnets/questions/QuestionEditor";
import { useSearchParams } from "react-router-dom";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";

interface AllQuestionsFormProps {
  questions: BaseAssignmentResponseDtoQuestionsItem[];
  canEdit: boolean;
  questionsLoading: boolean;
  questionRef: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  onQuestionsChange: (
    questions: BaseAssignmentResponseDtoQuestionsItem[],
  ) => void;
  updateAssignment: (params: {
    assignmentId: number;
    data: {
      deletedIds: number[];
      newlyCreated: any[];
      edited: Record<string, any>;
    };
  }) => void;
  onRegenerateQuestion: (questionId: number) => void;
  onAssignToStudent?: (assignmentId: number) => void;
  gradeLevel?: number;
}

const STORAGE_KEY = "edited-questions";

const AllQuestionsForm = ({
  questions,
  gradeLevel,
  canEdit,
  questionsLoading,
  questionRef,
  onQuestionsChange,
  updateAssignment,
  onRegenerateQuestion,
  onAssignToStudent,
}: AllQuestionsFormProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(
    null,
  );

  const [storedEdits, setStoredEdits] = useLocalStorage<
    BaseAssignmentResponseDtoQuestionsItem[]
  >(STORAGE_KEY, []);

  const [editedQuestion, setEditedQuestion] =
    useState<BaseAssignmentResponseDtoQuestionsItem | null>(null);
  const [originalQuestion, setOriginalQuestion] =
    useState<BaseAssignmentResponseDtoQuestionsItem | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<
    number | null
  >(null);

  const getOriginalQuestionFromStorage = useCallback((questionId: number) => {
    try {
      const stored = localStorage.getItem(`original-${questionId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Error retrieving original question:", error);
      return null;
    }
  }, []);

  const getRegenQuestionFromStorage = useCallback((questionId: number) => {
    try {
      const stored = localStorage.getItem(`regen-${questionId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Error retrieving regenerated question:", error);
      return null;
    }
  }, []);

  const cleanupStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);

    questions.forEach((q) => {
      localStorage.removeItem(`regen-${q.id}`);
      localStorage.removeItem(`show-versions-${q.id}`);
      localStorage.removeItem(`original-${q.id}`);
    });

    if (editingQuestionId) {
      localStorage.removeItem(`original-${editingQuestionId}`);
    }
  }, [questions, editingQuestionId]);

  useEffect(() => {
    if (onAssignToStudent && questions.length > 0) {
      const assignmentId = questions[0]?.assignmentId;
      if (assignmentId) {
        const originalAssignToStudent = onAssignToStudent;
        onAssignToStudent = (id) => {
          cleanupStorage();
          originalAssignToStudent(id);
        };
      }
    }
  }, [questions, onAssignToStudent, cleanupStorage]);

  const handleEditClick = (
    question: BaseAssignmentResponseDtoQuestionsItem,
  ) => {
    const existingOriginal = getOriginalQuestionFromStorage(question.id);

    if (!existingOriginal) {
      localStorage.setItem(`original-${question.id}`, JSON.stringify(question));
    }

    const originalFromStorage = existingOriginal || question;
    setOriginalQuestion({ ...originalFromStorage });

    setEditingQuestionId(question.id);
    setEditedQuestion({ ...question });
  };

  const handleSaveEdit = () => {
    if (!editedQuestion) return;

    const existingEditIndex = storedEdits.findIndex(
      (edit) => edit.id === editedQuestion.id,
    );

    if (existingEditIndex >= 0) {
      const newStoredEdits = [...storedEdits];
      newStoredEdits[existingEditIndex] = editedQuestion;
      setStoredEdits(newStoredEdits);
    } else {
      setStoredEdits([...storedEdits, editedQuestion]);
    }

    const newQuestions = questions.map((q) =>
      q.id === editedQuestion.id ? editedQuestion : q,
    );
    onQuestionsChange(newQuestions);
    setEditingQuestionId(null);
    setEditedQuestion(null);
    setOriginalQuestion(null);
  };

  const handleRevertEdit = () => {
    if (!originalQuestion || !editingQuestionId) return;

    setEditedQuestion({ ...originalQuestion });
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditedQuestion(null);
    setOriginalQuestion(null);
  };

  const handleDeleteQuestion = (questionId: number) => {
    const assignmentId = questions[0]?.assignmentId;
    if (assignmentId) {
      updateAssignment({
        assignmentId,
        data: {
          deletedIds: [questionId],
          newlyCreated: [],
          edited: {},
        },
      });

      setStoredEdits(storedEdits.filter((edit) => edit.id !== questionId));

      localStorage.removeItem(`original-${questionId}`);
      localStorage.removeItem(`regen-${questionId}`);
      localStorage.removeItem(`show-versions-${questionId}`);
    }
    setDeleteConfirmationId(null);
  };

  const getOriginalQuestion = (questionId: number) => {
    return getRegenQuestionFromStorage(questionId);
  };

  const renderQuestion = (
    question: BaseAssignmentResponseDtoQuestionsItem,
    index: number,
    gradeLevel: number | undefined,
  ) => {
    const isEditing = editingQuestionId === question.id;
    const regenOriginalQuestion = getOriginalQuestion(question.id);

    return (
      <div
        key={question.id}
        ref={(el) => (questionRef.current[question.id] = el)}
        className={`p-6 mb-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 relative transform transition-all duration-300 hover:-translate-y-1 
          ${
            searchParams.get("questionId") === question.id.toString()
              ? "ring-2 ring-indigo-500 dark:ring-indigo-400 shadow-lg shadow-indigo-500/20"
              : "shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.3)] dark:hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.3)]"
          }`}
      >
        <div className="relative">
          <div className="flex justify-between items-center gap-2 mb-4">
            {isEditing ? (
              <div className="flex items-center ml-auto gap-2">
                <Button
                  onClick={handleSaveEdit}
                  bgColor="dark:bg-green-900 dark:hover:bg-green-800 bg-green-100 hover:bg-green-200"
                  textColor="text-green-700 dark:text-green-100"
                  rounded="full"
                  size="sm"
                  title={t("assignments.generator.preview.save")}
                >
                  <Save className="w-4 h-4" />
                </Button>
                {originalQuestion &&
                  editedQuestion &&
                  JSON.stringify(originalQuestion) !==
                    JSON.stringify(editedQuestion) && (
                    <Button
                      onClick={handleRevertEdit}
                      bgColor="bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800"
                      textColor="text-blue-700 dark:text-blue-100"
                      rounded="full"
                      size="sm"
                      title={t("assignments.generator.preview.revert")}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                <Button
                  onClick={handleCancelEdit}
                  bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                  textColor="text-gray-700 dark:text-gray-100"
                  rounded="full"
                  size="sm"
                  title={t("assignments.generator.preview.cancel")}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                {(canEdit || questionsLoading) && (
                  <div className="flex justify-end gap-2">
                    {regenOriginalQuestion &&
                      JSON.stringify(regenOriginalQuestion) !==
                        JSON.stringify(question) && (
                        <Button
                          onClick={() => {
                            const newQuestions = questions.map((q) =>
                              q.id === question.id
                                ? {
                                    ...q,
                                    ...regenOriginalQuestion,
                                  }
                                : q,
                            );
                            onQuestionsChange(newQuestions);
                          }}
                          bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                          textColor="text-gray-700 dark:text-white"
                          rounded="full"
                          size="sm"
                          title={t(
                            "assignments.generator.preview.previousVersion",
                          )}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                    {regenOriginalQuestion && (
                      <Button
                        onClick={() => {
                          onRegenerateQuestion(question.id);
                          localStorage.setItem(
                            `show-versions-${question.id}`,
                            "true",
                          );
                        }}
                        bgColor="bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800"
                        textColor="text-green-700 dark:text-white"
                        rounded="full"
                        size="sm"
                        title={t(
                          "assignments.generator.preview.chooseVersions",
                        )}
                      >
                        <Files className="w-4 h-4" />
                        <Text variant="span" className="text-sm font-medium">
                          {t("assignments.generator.preview.chooseVersions")}
                        </Text>
                      </Button>
                    )}
                  </div>
                )}
                {(canEdit || questionsLoading) && (
                  <div className="flex justify-end items-center gap-2">
                    <Button
                      onClick={() => {
                        onRegenerateQuestion(question.id);
                      }}
                      bgColor="bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-600 dark:hover:bg-indigo-500"
                      textColor="text-indigo-700 dark:text-white"
                      rounded="full"
                      size="sm"
                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                      title={t("assignments.generator.preview.regenerate")}
                    >
                      <Wand className="w-4 h-4" />
                      <Text variant="span" className="text-sm font-medium">
                        {t("assignments.generator.preview.regenerate")}
                      </Text>
                    </Button>
                    <Button
                      onClick={() => handleEditClick(question)}
                      bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                      textColor="text-gray-700 dark:text-gray-100"
                      rounded="full"
                      size="sm"
                      title={t("assignments.generator.preview.edit")}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <div className="relative">
                      {deleteConfirmationId === question.id && (
                        <div className="absolute right-0 top-10 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 w-64">
                          <Text
                            variant="p"
                            className="text-sm text-gray-600 dark:text-gray-400 mb-4"
                          >
                            {t(
                              "assignments.generator.preview.deleteConfirmation",
                            )}
                          </Text>
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => setDeleteConfirmationId(null)}
                              bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                              size="sm"
                              textColor="text-gray-700 dark:text-gray-100"
                              rounded="md"
                            >
                              {t("common.cancel")}
                            </Button>
                            <Button
                              onClick={() => handleDeleteQuestion(question.id)}
                              bgColor="bg-red-500 hover:bg-red-600"
                              rounded="md"
                              textColor="text-white"
                              size="sm"
                            >
                              {t("common.delete")}
                            </Button>
                          </div>
                        </div>
                      )}
                      <Button
                        onClick={() => setDeleteConfirmationId(question.id)}
                        bgColor="bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800"
                        textColor="text-red-700 dark:text-red-100"
                        rounded="full"
                        size="sm"
                        title={t("assignments.generator.preview.delete")}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {isEditing ? (
            <QuestionEditor
              question={editedQuestion!}
              gradeLevel={gradeLevel}
              onQuestionChange={(changedQuestion) => {
                setEditedQuestion(changedQuestion);
              }}
            />
          ) : (
            <QuestionViewer index={index + 1} question={question} />
          )}
          <div className="flex justify-end items-center gap-2 mt-4 mx-1">
            <Text
              variant="span"
              className="text-gray-600 dark:text-gray-300 font-medium text-sm"
            >
              {t("assignments.generator.preview.points", {
                points: question.point,
              })}
            </Text>
          </div>
        </div>
      </div>
    );
  };

  const breakpointColumnsObj = {
    1536: 2,
    1024: 2,
    768: 1,
  };

  return (
    <Masonry breakpointCols={breakpointColumnsObj} className="flex gap-3">
      {questions.map((question, index) =>
        renderQuestion(question, index, gradeLevel),
      )}
    </Masonry>
  );
};

export default AllQuestionsForm;
